import type { AxiosError } from "axios";

export type ErrorResponseBodyType = {
  detail: string;
};

export const isErrorResponseBodyType = (
  o: unknown,
): o is ErrorResponseBodyType => {
  return (
    !!o &&
    typeof o === "object" &&
    (o as ErrorResponseBodyType).detail !== undefined
  );
};

/** Type guard for Axios errors (used by jsp platform client with throwOnError: true). */
export const isApiError = (o: unknown): o is AxiosError => {
  return !!o && typeof o === "object" && (o as AxiosError).isAxiosError;
};

/** Normalize AxiosError to { status, statusText, body } for handlers that expect the old ApiError shape. */
export function toJspApiError(e: AxiosError): {
  status: number;
  statusText: string;
  body: unknown;
} {
  const res = e.response;
  return {
    status: res?.status ?? 0,
    statusText: res?.statusText ?? e.message,
    body: res?.data,
  };
}

/**
 * Build a human-readable message from an AxiosError returned by the jsp
 * platform client. Falls back to `[status] statusText` when the response body
 * is not in the expected error shape; JSON-stringifies 422 validation details.
 */
export function getJspApiErrorMessage(e: AxiosError): string {
  const { status, statusText, body } = toJspApiError(e);
  if (body && isErrorResponseBodyType(body)) {
    return status === 422 ? JSON.stringify(body.detail) : body.detail;
  }
  return `[${status}] ${statusText}`;
}
